import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Chip,
    TablePagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Divider
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { theme } from '../../../theme';
import { superadminService } from '../../../services/superadmin.service';

const ACTOR_ROLE_LABELS = {
    admin: 'Admin',
    superadmin: 'Super administrateur',
    professional_responsable: 'Responsable du salon',
    professional_manager: 'Gestionnaire',
    professional_manager_employee: 'Gestionnaire / Employé',
};

const getActorRoleLabel = (role) => {
    if (!role) return null;
    if (ACTOR_ROLE_LABELS[role]) return ACTOR_ROLE_LABELS[role];

    return role
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const SalonHistory = ({ salonId }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [count, setCount] = useState(0);

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    const loadHistory = async () => {
        try {
            setLoading(true);
            const params = {
                limit: rowsPerPage,
                offset: page * rowsPerPage
            };
            const response = await superadminService.getSalonHistory(salonId, params);

            // Handle both paginated (object with results) and non-paginated (array) responses
            if (Array.isArray(response.data)) {
                setLogs(response.data);
                setCount(response.data.length);
            } else {
                setLogs(response.data.results || []);
                setCount(response.data.count || 0);
            }
        } catch (error) {
            console.error("Failed to load salon history", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (salonId) {
            loadHistory();
        }
    }, [salonId, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = (log) => {
        setSelectedLog(log);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedLog(null);
    };

    const getActionColor = (action) => {
        if (!action) return 'default';
        if (action.includes('create')) return 'success';
        if (action.includes('update')) return 'info';
        if (action.includes('delete')) return 'error';
        if (action.includes('login')) return 'primary';
        return 'default';
    };

    const getActionLabel = (action, backendDisplay) => {
        // Use backend display if it's a real sentence (doesn't contain dot like 'salon.create')
        if (backendDisplay && !backendDisplay.includes('.')) {
            return backendDisplay;
        }

        const labels = {
            'salon.create': 'Création Salon',
            'salon.update': 'Modif. Salon',
            'salon.delete': 'Suppr. Salon',
            'category.create': 'Création Catégorie',
            'category.update': 'Modif. Catégorie',
            'category.delete': 'Suppr. Catégorie',
            'service.create': 'Création Service',
            'service.update': 'Modif. Service',
            'service.delete': 'Suppr. Service',
            'team.create': 'Ajout Membre',
            'team.update': 'Modif. Membre',
            'team.delete': 'Suppr. Membre',
            'team.member_create': 'Ajout Employé',
            'team.member_update': 'Modif. Employé',
            'team.member_delete': 'Suppr. Employé',
            'schedule.create': 'Création Horaire',
            'schedule.update': 'Modif. Horaire',
            'schedule.delete': 'Suppr. Horaire',
            'appointment.create': 'Nouveau Rdv',
            'appointment.update': 'Modif. Rdv',
            'appointment.delete': 'Suppr. Rdv',
            'appointment.cancel': 'Annulation Rdv',
            'appointment.confirm': 'Confirm. Rdv',
            'other.create': 'Création (Autre)',
            'other.update': 'Modif. (Autre)',
            'other.delete': 'Suppr. (Autre)',
        };
        return labels[action] || action || 'Inconnu';
    };

    const renderChanges = (values) => {
        if (!values || Object.keys(values).length === 0) {
            return <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>;
        }

        return (
            <Box sx={{ bgcolor: theme.colors.background.tertiary, p: 2, borderRadius: 1, maxHeight: '300px', overflow: 'auto' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
                    {JSON.stringify(values, null, 2)}
                </pre>
            </Box>
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                    Historique d'activité
                </Typography>
                <Tooltip title="Rafraîchir">
                    <IconButton onClick={loadHistory} disabled={loading} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden', border: `1px solid ${theme.colors.border.light}`, boxShadow: theme.boxShadow.sm, borderRadius: theme.borderRadius.lg }}>
                <TableContainer>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Utilisateur</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }} align="center">Détails</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">Aucun historique disponible.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.map((log) => (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={log.id}
                                        onClick={() => {
                                            if (window.innerWidth < 900) { // md breakpoint is 900
                                                handleOpenDialog(log);
                                            }
                                        }}
                                        sx={{ cursor: { xs: 'pointer', md: 'default' } }}
                                    >
                                        <TableCell sx={{ minWidth: { xs: 100, md: 150 } }}>
                                            {format(new Date(log.timestamp), 'dd MMM yyyy HH:mm', { locale: fr })}
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {log.user_name || 'Système'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.user_email}
                                            </Typography>
                                            {getActorRoleLabel(log.actor_role) && (
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        label={getActorRoleLabel(log.actor_role)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getActionLabel(log.action, log.action_display)}
                                                size="small"
                                                color={getActionColor(log.action)}
                                                variant="outlined"
                                                sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: 'inherit' } }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 350, display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography variant="body2" noWrap title={log.description}>
                                                {log.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            {(log.old_values || log.new_values) && (
                                                <Tooltip title="Voir les détails">
                                                    <IconButton size="small" onClick={() => handleOpenDialog(log)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Lignes par page :"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                />
            </Paper>

            {/* Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.colors.border.light}`, fontWeight: 800 }}>
                    Détails de l'activité
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedLog && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 700, textTransform: 'uppercase' }}>Date</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {format(new Date(selectedLog.timestamp), 'dd MMMM yyyy HH:mm', { locale: fr })}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 700, textTransform: 'uppercase' }}>Système / Utilisateur</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {selectedLog.user_name || 'Système'}
                                    </Typography>
                                    {selectedLog.user_email && (
                                        <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>
                                            ({selectedLog.user_email})
                                        </Typography>
                                    )}
                                </Box>
                                {getActorRoleLabel(selectedLog.actor_role) && (
                                    <Box sx={{ mt: 0.75 }}>
                                        <Chip
                                            label={getActorRoleLabel(selectedLog.actor_role)}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 700, textTransform: 'uppercase' }}>Action</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={getActionLabel(selectedLog.action, selectedLog.action_display)}
                                        size="small"
                                        color={getActionColor(selectedLog.action)}
                                        sx={{ fontWeight: 700 }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 700, textTransform: 'uppercase' }}>Description</Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, p: 1.5, bgcolor: theme.colors.background.secondary, borderRadius: 1 }}>
                                    {selectedLog.description}
                                </Typography>
                            </Grid>

                            {(selectedLog.old_values || selectedLog.new_values) && (
                                <>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1rem' }}>Modifications détaillées</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.colors.error[600] }}>
                                            Anciennes valeurs
                                        </Typography>
                                        {renderChanges(selectedLog.old_values)}
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.colors.success[600] }}>
                                            Nouvelles valeurs
                                        </Typography>
                                        {renderChanges(selectedLog.new_values)}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.colors.border.light}` }}>
                    <Button onClick={handleCloseDialog} variant="contained" fullWidth={window.innerWidth < 600}>
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SalonHistory;
